import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

function KindCard({ title, desc, icon, iconBg, iconColor, onClick, variant = 'outlined' }) {
  return (
    <MDButton
      variant={variant}
      color="dark"
      fullWidth
      onClick={onClick}
      sx={(theme) => ({
        borderRadius: '18px',
        textTransform: 'none',
        fontWeight: 900,
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        px: 1.5,
        py: 1.3,
        gap: 1.25,
        borderWidth: variant === 'outlined' ? '1.5px' : undefined,
        background:
          variant === 'contained'
            ? `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.14)}, ${alpha(
                theme.palette.info.main,
                0.06
              )})`
            : 'transparent',
        '&:hover': {
          borderWidth: variant === 'outlined' ? '1.5px' : undefined,
          backgroundColor: alpha(
            theme.palette.grey[900],
            theme.palette.mode === 'dark' ? 0.08 : 0.03
          ),
          transform: 'translateY(-1px)',
        },
        '&:active': { transform: 'translateY(0px)' },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.18)}`,
        },
      })}
    >
      {/* Icon badge */}
      <MDBox
        sx={(theme) => ({
          width: 44,
          height: 44,
          borderRadius: '16px',
          display: 'grid',
          placeItems: 'center',
          flex: '0 0 auto',
          background: iconBg || alpha(theme.palette.info.main, 0.12),
          border: `1px solid ${alpha(
            theme.palette.common.black,
            theme.palette.mode === 'dark' ? 0.25 : 0.08
          )}`,
        })}
      >
        <Icon sx={{ fontSize: 20, color: iconColor || 'inherit' }}>{icon}</Icon>
      </MDBox>

      {/* Texts */}
      <MDBox sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ display: 'block', lineHeight: 1.15 }}
        >
          {title}
        </MDTypography>
        <MDTypography
          variant="caption"
          sx={{ display: 'block', opacity: 0.78, mt: 0.35, lineHeight: 1.25 }}
        >
          {desc}
        </MDTypography>
      </MDBox>

      {/* Right chevron */}
      <MDBox sx={{ display: 'grid', placeItems: 'center', opacity: 0.55 }}>
        <Icon sx={{ fontSize: 18 }}>chevron_right</Icon>
      </MDBox>
    </MDButton>
  );
}

export function SessionKindModal({ open, onClose, onPick, tokens }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const t = {
    // açık tema odaklı “premium” defaults
    isDark: tokens?.isDark ?? false,
    border: tokens?.border || alpha('#0b1220', 0.12),
    surface: tokens?.surface || '#ffffff',
    paper: tokens?.isDark ? alpha('#0b1220', 0.92) : alpha('#ffffff', 0.98),
  };

  const handlePick = (kind) => {
    onPick?.(kind);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          overflow: 'hidden',
          background: t.paper,
          border: `1px solid ${t.border}`,
          boxShadow: isMobile ? 'none' : '0 22px 60px rgba(0,0,0,.16), 0 10px 22px rgba(0,0,0,.12)',
          backgroundImage: 'none',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: isMobile ? 'rgba(0,0,0,.55)' : 'rgba(0,0,0,.45)',
          backdropFilter: 'blur(6px)',
        },
      }}
      aria-labelledby="session-kind-title"
      aria-describedby="session-kind-desc"
    >
      <DialogTitle sx={{ pb: 1.25 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1.2}>
          <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
            <MDBox
              sx={{
                width: 40,
                height: 40,
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                background: alpha(theme.palette.info.main, 0.12),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                flex: '0 0 auto',
              }}
            >
              <Icon sx={{ fontSize: 20, color: theme.palette.info.main }}>event_available</Icon>
            </MDBox>

            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography
                id="session-kind-title"
                variant="h6"
                fontWeight="bold"
                sx={{ lineHeight: 1.15 }}
              >
                Seans tipi seç
              </MDTypography>
              <MDTypography
                id="session-kind-desc"
                variant="caption"
                sx={{ opacity: 0.75, display: 'block', mt: 0.35 }}
              >
                Oluşturacağın seansın ücrete ve hak tüketimine etkisini belirler.
              </MDTypography>
            </MDBox>
          </MDBox>

          <MDButton
            variant="text"
            color="secondary"
            onClick={onClose}
            sx={{
              minWidth: 44,
              width: 44,
              height: 44,
              borderRadius: '14px',
              '&:hover': {
                backgroundColor: alpha(
                  theme.palette.grey[900],
                  theme.palette.mode === 'dark' ? 0.12 : 0.04
                ),
              },
            }}
          >
            <Icon>close</Icon>
          </MDButton>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1.75 }}>
        <MDBox sx={{ display: 'grid', gap: 1.25 }}>
          <KindCard
            title="Paket seansı"
            desc="Üyenin paket haklarından düşer. Pakete dahil seanslar ve telafi seansları bu gruptadır."
            icon="redeem"
            iconBg={alpha(theme.palette.info.main, 0.12)}
            iconColor={theme.palette.info.main}
            onClick={() => handlePick('PACKAGE')}
          />

          <KindCard
            title="Ücretli hizmet"
            desc="Pakete dahil değildir. Tek seans/ek hizmet gibi ayrıca ücretlendirilir ve ödeme kaydı oluşturulabilir."
            icon="payments"
            iconBg={alpha(theme.palette.success.main, 0.12)}
            iconColor={theme.palette.success.main}
            onClick={() => handlePick('PAID')}
          />

          <KindCard
            title="Ücretsiz hizmet"
            desc="Ücret alınmaz. Değerlendirme seansı, tanışma/randevu veya bilgilendirme görüşmesi gibi kayıtlar için idealdir."
            icon="volunteer_activism"
            iconBg={alpha(theme.palette.warning.main, 0.14)}
            iconColor={theme.palette.warning.main}
            onClick={() => handlePick('FREE')}
          />
        </MDBox>

        {/* Light info box (eski gri yerine premium açık) */}
        <MDBox
          sx={{
            mt: 1.5,
            p: 1.25,
            borderRadius: '18px',
            border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
            background: `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(
              theme.palette.info.main,
              0.04
            )})`,
          }}
        >
          <MDBox display="flex" gap={1} alignItems="flex-start">
            <Icon sx={{ fontSize: 18, color: theme.palette.info.main, mt: '2px' }}>info</Icon>
            <MDTypography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.35 }}>
              <b>Paket seansı</b> hak tüketir • <b>Ücretli</b> ek ücret/ödeme akışına uygundur •{' '}
              <b>Ücretsiz</b> kayıt amaçlıdır (değerlendirme, randevu, bilgilendirme).
            </MDTypography>
          </MDBox>
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0.75 }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          fullWidth={isMobile}
          sx={{
            borderRadius: '16px',
            textTransform: 'none',
            fontWeight: 900,
            height: 44,
          }}
        >
          Vazgeç
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
